/**
 * Resource budgets + the misbehaving list — Command Plane Phase 6.
 *
 * Doc 34 §4 orders the console by what a customer actually asks. Item 3 is
 * *"Misbehaviour — instances over budget, over-running their window, or
 * failing; **with the intervention taken, not just the reading**."* That last
 * clause is why every row here shows `actionTaken` rather than only the
 * numbers: an operator needs to know whether the platform DID something, and
 * `none` is the common and honest answer while enforcement is `observe`.
 *
 * The panel deliberately shows the ceiling next to the observation. A bare
 * "247%" means nothing; "247% against a 150% ceiling, sustained over 5
 * readings" is a decision.
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Gauge, Loader2, RefreshCw, ShieldCheck } from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  listEdgeBreaches,
  listEdgeBudgets,
  upsertEdgeBudget,
  type EdgeBudgetBreach,
  type EdgeEnforcement,
  type EdgeResourceBudget,
} from '@/lib/api';

/**
 * The settable enforcement modes. `throttle` was removed in release 3.1: no
 * agent ever applied it, so it did exactly what observe does while the console
 * offered it as a lever (an inert surface presented as a control - the exact
 * pattern doc 34 catalogues), and the API now refuses it. A breach recorded
 * before 3.1 may still say `throttle` in "Action taken"; that renders as a
 * plain badge.
 */
const ENFORCEMENT_HELP: Record<EdgeEnforcement, string> = {
  observe: 'Record and audit only. Nothing is ever stopped.',
  pause: 'Pauses the agent via the kill switch once a breach is sustained.',
};

const ENFORCEMENT_VARIANT: Record<EdgeEnforcement, 'glass' | 'warning' | 'critical'> = {
  observe: 'glass',
  pause: 'critical',
};

/** An observation rendered against its ceiling, or an em dash when unmeasured. */
export function formatAgainstCeiling(
  observed: number | null,
  ceiling: number | null,
  unit: string,
): string {
  if (observed == null) return '—';
  const o = unit === '%' ? observed.toFixed(1) : String(observed);
  if (ceiling == null) return `${o}${unit}`;
  const c = unit === '%' ? ceiling.toFixed(0) : String(ceiling);
  return `${o}${unit} / ${c}${unit}`;
}

export function BudgetWatchdogPanel(): JSX.Element {
  const [budgets, setBudgets] = useState<EdgeResourceBudget[]>([]);
  const [breaches, setBreaches] = useState<EdgeBudgetBreach[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyComponent, setBusyComponent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const [b, br] = await Promise.all([listEdgeBudgets(), listEdgeBreaches()]);
      setBudgets(b.budgets);
      setBreaches(br.breaches);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load budgets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  async function setEnforcement(component: string, next: EdgeEnforcement): Promise<void> {
    // Turning enforcement on is the moment the platform gains permission to
    // stop a customer's agent, so it is confirmed rather than a silent toggle.
    if (next === 'pause' && !window.confirm(
      `Allow the watchdog to PAUSE ${component} agents automatically?\n\n` +
      `Once a breach is sustained past the configured number of readings, the ` +
      `agent is stopped via the kill switch without asking. Scanning stops until ` +
      `someone resumes it. This is audited, and can be undone from the pod drawer.`,
    )) return;
    setBusyComponent(component);
    setError(null);
    try {
      await upsertEdgeBudget(component as EdgeResourceBudget['component'], { enforcement: next });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to update budget');
    } finally {
      setBusyComponent(null);
    }
  }

  return (
    <div className="space-y-4">
      {error && (
        <GlassCard className="p-3 border-sev-critical/40">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-sev-critical">{error}</span>
            <Button size="sm" variant="glass" onClick={() => setError(null)}>Dismiss</Button>
          </div>
        </GlassCard>
      )}

      {/* ── Misbehaving ─────────────────────────────────────────────── */}
      <GlassCard className="p-3">
        <CardHeader className="p-0 mb-2">
          <CardTitle className="text-sm inline-flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4" /> Misbehaving agents
          </CardTitle>
          <CardDescription clamp className="text-[11px]">
            Agents over their class budget for long enough to count. A single spike is
            not listed - a scheduled scan is supposed to be busy.
          </CardDescription>
        </CardHeader>

        {breaches.length === 0 ? (
          <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
            {/* emerald-400, not a `sev-*` token: the palette defines only
                critical/high/medium/low, so `text-sev-success` compiled to
                nothing and the icon rendered uncoloured. */}
            <ShieldCheck className="h-3 w-3 text-emerald-400" />
            No agent is over budget.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="py-1 pr-3 font-normal">Pod</th>
                <th className="py-1 pr-3 font-normal">Component</th>
                <th className="py-1 pr-3 font-normal whitespace-nowrap">CPU / ceiling</th>
                <th className="py-1 pr-3 font-normal whitespace-nowrap">RSS / ceiling</th>
                <th className="py-1 pr-3 font-normal whitespace-nowrap">Readings</th>
                <th className="py-1 pr-3 font-normal">Action taken</th>
                <th className="py-1 pr-3 font-normal whitespace-nowrap">Since</th>
              </tr>
            </thead>
            <tbody>
              {breaches.map((b) => (
                <tr key={b.id} className="border-t border-white/5">
                  <td className="py-1.5 pr-3 font-mono text-[11px]">{b.podId}</td>
                  <td className="py-1.5 pr-3">{b.component}</td>
                  <td className="py-1.5 pr-3 font-mono text-[11px]">
                    {formatAgainstCeiling(b.observedCpuPercent, b.ceilingCpuPercent, '%')}
                  </td>
                  <td className="py-1.5 pr-3 font-mono text-[11px]">
                    {formatAgainstCeiling(b.observedRssMb, b.ceilingRssMb, 'MiB')}
                  </td>
                  <td className="py-1.5 pr-3 tabular">{b.consecutiveSamples}</td>
                  <td className="py-1.5 pr-3" title={b.actionDetail ?? ''}>
                    {/*
                      "none" is the honest common case under observe. Rendering
                      it as a dash would hide that the platform saw this and
                      deliberately did nothing.
                    */}
                    <Badge variant={b.actionTaken === 'pause' ? 'critical' : 'glass'}>
                      {b.actionTaken === 'none' ? 'recorded only' : b.actionTaken}
                    </Badge>
                  </td>
                  <td className="py-1.5 pr-3 whitespace-nowrap text-muted-foreground">
                    {new Date(b.firstObservedAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </GlassCard>

      {/* ── Budgets ─────────────────────────────────────────────────── */}
      <GlassCard className="p-3">
        <CardHeader className="p-0 mb-2">
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="text-sm inline-flex items-center gap-1.5">
                <Gauge className="h-4 w-4" /> Resource budgets
              </CardTitle>
              <CardDescription clamp className="text-[11px]">
                Ceilings per agent class. A breach needs the shown number of consecutive
                readings, which is how a scheduled scan is told apart from a runaway.
              </CardDescription>
            </div>
            <Button size="sm" variant="glass" disabled={loading} onClick={() => void refresh()}>
              {loading ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1.5" />}
              Refresh
            </Button>
          </div>
        </CardHeader>

        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-1 pr-3 font-normal">Component</th>
              <th className="py-1 pr-3 font-normal whitespace-nowrap">CPU ceiling</th>
              <th className="py-1 pr-3 font-normal whitespace-nowrap">RSS ceiling</th>
              <th className="py-1 pr-3 font-normal whitespace-nowrap">Sustained</th>
              <th className="py-1 pr-3 font-normal">Enforcement</th>
              <th className="py-1 pr-3 font-normal">Set</th>
            </tr>
          </thead>
          <tbody>
            {budgets.map((b) => (
              <tr key={b.component} className="border-t border-white/5">
                <td className="py-1.5 pr-3">{b.component}</td>
                <td className="py-1.5 pr-3 font-mono text-[11px]">
                  {b.cpuPercentCeiling == null ? '—' : `${b.cpuPercentCeiling}%`}
                </td>
                <td className="py-1.5 pr-3 font-mono text-[11px]">
                  {b.rssMbCeiling == null ? '—' : `${b.rssMbCeiling} MiB`}
                </td>
                <td className="py-1.5 pr-3 tabular">{b.sustainedSamples}</td>
                <td className="py-1.5 pr-3">
                  {/* `?? 'glass'`: a value this build does not know still renders, never crashes. */}
                  <span title={ENFORCEMENT_HELP[b.enforcement] ?? ''}>
                    <Badge variant={ENFORCEMENT_VARIANT[b.enforcement] ?? 'glass'}>{b.enforcement}</Badge>
                  </span>
                </td>
                <td className="py-1.5 pr-3">
                  <div className="flex flex-wrap gap-1.5 items-center">
                    {/*
                      `isDefault` is surfaced because "a platform default nobody
                      has looked at" and "a ceiling we chose" are different
                      facts, and only one of them is a decision.
                    */}
                    {b.isDefault && (
                      <span className="text-[10px] text-muted-foreground">platform default</span>
                    )}
                    {(['observe', 'pause'] as const).map((mode) => (
                      b.enforcement === mode ? null : (
                        <Button
                          data-action="disc.adm_edge_fleet.a08"
                          key={mode}
                          size="sm"
                          variant={mode === 'pause' ? 'destructive' : 'glass'}
                          disabled={busyComponent === b.component}
                          onClick={() => void setEnforcement(b.component, mode)}
                        >
                          {busyComponent === b.component
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : mode === 'pause' ? 'Allow auto-pause' : 'Observe only'}
                        </Button>
                      )
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </GlassCard>
    </div>
  );
}
